import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Container, Box, CssBaseline, CircularProgress } from '@mui/material';
import Navigation from './components/Navigation';
import UserProfile from './components/UserProfile';
import GlobalLoader from './components/GlobalLoader';
import DocumentTitle from './components/DocumentTitle';
import { useConfig } from './hooks/useConfig';


import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { SettingsProvider } from './contexts/SettingsContext';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded pages
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const GoogleCallbackPage = lazy(() => import('./pages/GoogleCallbackPage'));

/**
 * HomePage component with dynamic configuration
 * Uses configuration context to display app-specific content
 */
function HomePage() {
  const { app, loading: configLoading, error: configError } = useConfig();
  
  return (
    <>
      <GlobalLoader />
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h1>Welcome to {configLoading ? 'Loading...' : app.name}</h1>
          <UserProfile />
          <p>
            {configLoading 
              ? 'Loading application details...' 
              : configError 
                ? 'This is the initial framework for the application.'
                : app.shortDescription
            }
          </p>
          <Box sx={{ mt: 2, fontSize: '0.8rem', color: 'text.secondary' }}>
            Version {configLoading ? 'Loading...' : app.version}
          </Box>
          {configError && (
            <Box sx={{ mt: 1, fontSize: '0.7rem', color: 'error.main' }}>
              Configuration loading failed - using defaults
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
}

function App() {
  // State for ResourcesPage modal
  const [resourcesModalOpen, setResourcesModalOpen] = useState(false);

  // Handler for opening/closing ResourcesPage modal
  const handleResourcesModal = (isOpen) => {
    setResourcesModalOpen(isOpen);
  };

  return (
    <ErrorBoundary>
      <ConfigProvider>
        <AuthProvider>
          <SettingsProvider>
            <NotificationProvider>
          <CssBaseline />
          <DocumentTitle />
          <Router>
            <Navigation onOpenResources={() => handleResourcesModal(true)} />
        
        
        
        
              <Routes>
            <Route path="/" element={<ChatPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/pricing" element={
              <Suspense fallback={<CircularProgress sx={{ mt: 4 }} />}>
                <PricingPage />
              </Suspense>
            } />

            <Route path="/payment/success" element={
              <Suspense fallback={<CircularProgress sx={{ mt: 4 }} />}>
                <PaymentSuccessPage />
              </Suspense>
            } />
            <Route path="/auth/callback" element={
              <Suspense fallback={<CircularProgress sx={{ mt: 4 }} />}>
                <GoogleCallbackPage />
              </Suspense>
            } />
        </Routes>

        {/* ResourcesPage Modal */}
        <Suspense fallback={<CircularProgress sx={{ mt: 4 }} />}>
          <ResourcesPage 
            open={resourcesModalOpen} 
            onClose={() => handleResourcesModal(false)} 
          />
        </Suspense>
      </Router>
            </NotificationProvider>
          </SettingsProvider>
        </AuthProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
